// apps/admin/src/features/billing/MarkTransferredModal.tsx
//
// Phase 8.2 Wave 2 Task 09 — Mark Transferred modal for Payouts queue.
//
// Per CONTEXT.md / API-CONTRACTS.md §POST /api/v1/admin/payouts/:id/mark-transferred
//   - Payouts page (vendor payout state machine + KYC gate).
//   - Payout state machine: Pending → Processing → Completed/Failed.
//                 Mark Transferred persists transferredBy/At/transferReference/transferProofUrl.
//   - Single approve per row (NO bulk) — modal scope is per-payout.
//   - Failed = NO auto-retry; earnings rollback on reject.
//   - Pre-KYC vendor payouts blocked (KYC gate enforced server-side).
//
// Behaviour contract:
//   1. Modal opens with empty transferReference + no file selected.
//   2. transferReference REQUIRED (min 3, max 100 per contract zod).
//   3. Optional bank-statement file upload (PDF / JPEG / PNG only, ≤5MB).
//      File is uploaded first to /api/v1/files/upload → returns file_id which
//      is converted to the proof URL `/api/v1/files/{file_id}` and sent as
//      `transfer_proof_url` on the mark-transferred POST.
//   4. Optional notes textarea (max 500 chars per contract).
//   5. On submit → POST /api/v1/admin/payouts/:id/mark-transferred with
//      Idempotency-Key (consistent with WithdrawQueuePage approve pattern +
//      INTERFACE-STANDARDS mutating verb policy).
//   6. Error mapping (per Block 3 of API contract):
//        409 INVALID_TRANSITION → toast warning + close modal so list refreshes.
//        422 TRANSFER_REFERENCE_REQUIRED → inline form error (defensive).
//        Other → toast error, modal stays open for retry.
//
// Test IDs (from PLAN/task-09.md <test_ids>):
//   - payout-mark-transferred-modal       — modal root
//   - payout-transfer-reference-input     — reference text input
//   - payout-mark-transferred-submit-btn  — submit button
//
// vg-binding: PLAN:task-09
// vg-binding: P8.D-86
// vg-binding: P8.D-190
// vg-binding: P8.D-451
// vg-binding: P8.D-452
// vg-binding: BR-1
// vg-binding: INTERFACE-STANDARDS:error-shape
// vg-binding: INTERFACE-STANDARDS:idempotency-key

import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  useToast,
} from '@printway/ui';

import { ApiError, apiClient } from '../../lib/apiClient.js';
import { uploadFileAbsoluteUrl } from '../../lib/uploadFile.js';
import { useTranslation } from '../../i18n/useTranslation.js';

// ---------------------------------------------------------------------------
// Constants — file-upload contract (mirror BE /api/v1/files/upload guard)
// ---------------------------------------------------------------------------

const MAX_PROOF_BYTES = 5 * 1024 * 1024; // 5MB + BE handler cap.
const ACCEPTED_PROOF_MIMES: ReadonlySet<string> = new Set([
  'application/pdf',
  'image/jpeg',
  'image/png',
]);
const ACCEPTED_PROOF_EXTS = ['.pdf', '.jpg', '.jpeg', '.png'];

const TRANSFER_REF_MIN = 3;
const TRANSFER_REF_MAX = 100;
const NOTES_MAX = 500;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface MarkTransferredPayoutSummary {
  id: string;
  vendor_name: string;
  /** USD cents — same canonical convention as listPayouts payload. */
  net_cents: number;
}

export interface MarkTransferredModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payout: MarkTransferredPayoutSummary | null;
  /** Fired after a successful mark-transferred so the parent can invalidate cache. */
  onSubmitted?: () => void;
}

interface MarkTransferredResponse {
  id: string;
  status: 'Completed';
  bank_reference: string;
  completed_at: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function newIdempotencyKey(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `mark-transferred-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function formatUsdCentsCompact(cents: number): string {
  const dollars = cents / 100;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(dollars);
}

/**
 * Upload the optional bank-statement proof via the unified upload client
 * (`POST /api/v1/files`, scope `topup-receipt`). Returns the canonical
 * proof URL on success, `null` on failure (caller surfaces inline error).
 * dbg-20260622-unified-upload — replaced the hand-rolled fetch + `file_id`
 * parsing with the shared `uploadFileAbsoluteUrl`.
 */
async function uploadProofFile(file: File): Promise<string | null> {
  try {
    return await uploadFileAbsoluteUrl({ file, scope: 'topup-receipt' });
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MarkTransferredModal({
  open,
  onOpenChange,
  payout,
  onSubmitted,
}: MarkTransferredModalProps): JSX.Element | null {
  const { toast } = useToast();
  const { t } = useTranslation('finance');
  const [transferReference, setTransferReference] = useState('');
  const [notes, setNotes] = useState('');
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [proofError, setProofError] = useState<string | null>(null);
  const [refError, setRefError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // G12: one key PER ACTION-CLICK, held in state — a retry after a network
  // error re-sends the SAME key so the server dedupes instead of settling the
  // vendor ledger twice. Regenerated on each modal open.
  const [idempotencyKey, setIdempotencyKey] = useState<string>(() =>
    newIdempotencyKey(),
  );
  useEffect(() => {
    if (open) setIdempotencyKey(newIdempotencyKey());
  }, [open]);

  // Reset on open ↔ close transition.
  useEffect(() => {
    if (!open) {
      setTransferReference('');
      setNotes('');
      setProofFile(null);
      setProofError(null);
      setRefError(null);
      setSubmitting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [open]);

  if (!payout) return null;

  function handlePickFile(file: File | null): void {
    setProofError(null);
    if (!file) {
      setProofFile(null);
      return;
    }
    // Validate MIME — extension fallback because some browsers report
    // `application/octet-stream` on drag-drop from certain OSes.
    const lowerName = file.name.toLowerCase();
    const looksAcceptedByExt = ACCEPTED_PROOF_EXTS.some((ext) =>
      lowerName.endsWith(ext),
    );
    if (!ACCEPTED_PROOF_MIMES.has(file.type) && !looksAcceptedByExt) {
      setProofError(t('payout.markTransferred.invalidFormat'));
      setProofFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (file.size > MAX_PROOF_BYTES) {
      setProofError(
        t('payout.markTransferred.fileTooLarge', {
          size: (file.size / 1024 / 1024).toFixed(2),
        }),
      );
      setProofFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setProofFile(file);
  }

  function handleClearFile(): void {
    setProofFile(null);
    setProofError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function handleSubmit(): Promise<void> {
    if (!payout) return;
    const trimmedRef = transferReference.trim();
    if (trimmedRef.length < TRANSFER_REF_MIN) {
      setRefError(
        t('payout.markTransferred.refTooShort', { min: TRANSFER_REF_MIN }),
      );
      return;
    }
    if (trimmedRef.length > TRANSFER_REF_MAX) {
      setRefError(
        t('payout.markTransferred.refTooLong', { max: TRANSFER_REF_MAX }),
      );
      return;
    }
    setRefError(null);
    setSubmitting(true);

    let proofUrl: string | null = null;
    if (proofFile) {
      proofUrl = await uploadProofFile(proofFile);
      if (!proofUrl) {
        setSubmitting(false);
        setProofError(t('payout.markTransferred.uploadFailed'));
        return;
      }
    }

    try {
      const body: Record<string, unknown> = {
        bank_reference: trimmedRef,
      };
      if (proofUrl) body.transfer_proof_url = proofUrl;
      const trimmedNotes = notes.trim();
      if (trimmedNotes) body.internal_note = trimmedNotes;

      await apiClient.post<MarkTransferredResponse>(
        `/api/v1/admin/payouts/${encodeURIComponent(payout.id)}/mark-transferred`,
        body,
        { idempotencyKey },
      );

      toast({
        variant: 'success',
        title: t('payout.markTransferred.toast.successTitle'),
        description: t('payout.markTransferred.toast.successDesc', {
          vendor: payout.vendor_name,
          amount: formatUsdCentsCompact(payout.net_cents),
        }),
      });
      onOpenChange(false);
      onSubmitted?.();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409) {
          toast({
            variant: 'warning',
            title: t('payout.markTransferred.toast.staleTitle'),
            description:
              err.message ||
              t('payout.markTransferred.toast.staleDesc'),
          });
          onOpenChange(false);
          onSubmitted?.();
          return;
        }
        if (
          err.status === 422 &&
          err.code === 'TRANSFER_REFERENCE_REQUIRED'
        ) {
          setRefError(
            err.message || t('payout.markTransferred.refRequired'),
          );
          return;
        }
        toast({
          variant: 'error',
          title: t('payout.markTransferred.toast.failTitle'),
          description: err.message,
        });
        return;
      }
      toast({
        variant: 'error',
        title: t('payout.markTransferred.toast.networkErrorTitle'),
        description:
          err instanceof Error
            ? err.message
            : t('payout.markTransferred.toast.networkErrorDesc'),
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (submitting) return;
        onOpenChange(next);
      }}
      aria-label={t('payout.markTransferred.ariaLabel', {
        vendor: payout.vendor_name,
      })}
    >
      <div
        data-testid="payout-mark-transferred-modal"
        className="flex flex-col gap-4"
      >
        <DialogHeader>
          <DialogTitle>
            {t('payout.markTransferred.title', { vendor: payout.vendor_name })}
          </DialogTitle>
          <p className="mt-1 text-sm text-pw-text-muted">
            {t('payout.markTransferred.subtitleBefore')}
            <strong>{t('payout.markTransferred.completedLabel')}</strong>
            {t('payout.markTransferred.subtitleAfter')}
          </p>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!submitting) void handleSubmit();
          }}
        >
          <div className="rounded-md border border-pw-border bg-pw-surface p-3 text-sm">
            <div className="flex justify-between">
              <span className="text-pw-text-muted">
                {t('payout.markTransferred.fields.netAmount')}
              </span>
              <span className="font-semibold text-pw-text">
                {formatUsdCentsCompact(payout.net_cents)} USD
              </span>
            </div>
            <div className="mt-1 flex justify-between">
              <span className="text-pw-text-muted">
                {t('payout.markTransferred.fields.vendor')}
              </span>
              <span className="text-pw-text">{payout.vendor_name}</span>
            </div>
          </div>

          <div>
            <label
              htmlFor="payout-transfer-reference-input"
              className="mb-1 block text-sm font-medium text-pw-text"
            >
              {t('payout.markTransferred.fields.transferRef')}{' '}
              <span className="text-rose-600 dark:text-rose-300">*</span>
            </label>
            <Input
              id="payout-transfer-reference-input"
              data-testid="payout-transfer-reference-input"
              value={transferReference}
              onChange={(e) => {
                setTransferReference(e.target.value);
                if (refError) setRefError(null);
              }}
              placeholder={t('payout.markTransferred.fields.transferRefPlaceholder')}
              aria-invalid={refError ? 'true' : 'false'}
              disabled={submitting}
              maxLength={TRANSFER_REF_MAX + 5}
            />
            {refError ? (
              <p
                className="mt-1 text-xs text-rose-600 dark:text-rose-300"
                role="alert"
                data-testid="payout-transfer-reference-error"
              >
                {refError}
              </p>
            ) : (
              <p className="mt-1 text-xs text-pw-text-muted">
                {t('payout.markTransferred.fields.transferRefHelp')}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="payout-proof-file-input"
              className="mb-1 block text-sm font-medium text-pw-text"
            >
              {t('payout.markTransferred.fields.proof')}
            </label>
            <input
              id="payout-proof-file-input"
              ref={fileInputRef}
              data-testid="payout-proof-file-input"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              disabled={submitting}
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null;
                handlePickFile(file);
              }}
              className="block w-full rounded-md border border-pw-border bg-pw-bg p-2 text-sm file:mr-3 file:rounded file:border-0 file:bg-pw-primary/10 file:px-3 file:py-1 file:text-sm file:font-medium file:text-pw-primary hover:file:bg-pw-primary/20"
            />
            {proofFile ? (
              <div
                className="mt-2 flex items-center justify-between rounded-md border border-pw-border bg-pw-surface px-3 py-2 text-xs"
                data-testid="payout-proof-file-selected"
              >
                <span className="truncate text-pw-text">
                  {proofFile.name} ·{' '}
                  {(proofFile.size / 1024).toFixed(1)} KB
                </span>
                <button
                  type="button"
                  className="ml-3 text-rose-600 dark:text-rose-300 hover:underline"
                  onClick={handleClearFile}
                  disabled={submitting}
                >
                  {t('payout.markTransferred.clearFile')}
                </button>
              </div>
            ) : null}
            {proofError ? (
              <p
                className="mt-1 text-xs text-rose-600 dark:text-rose-300"
                role="alert"
                data-testid="payout-proof-file-error"
              >
                {proofError}
              </p>
            ) : (
              <p className="mt-1 text-xs text-pw-text-muted">
                {t('payout.markTransferred.fields.proofHelp')}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="payout-mark-transferred-notes"
              className="mb-1 block text-sm font-medium text-pw-text"
            >
              {t('payout.markTransferred.fields.notes')}
            </label>
            <textarea
              id="payout-mark-transferred-notes"
              data-testid="payout-mark-transferred-notes"
              className="block h-20 w-full rounded-md border border-pw-border bg-pw-bg p-2 text-sm focus:border-pw-primary focus:outline-none focus:ring-2 focus:ring-pw-primary/20"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={NOTES_MAX}
              placeholder={t('payout.markTransferred.fields.notesPlaceholder')}
              disabled={submitting}
            />
            <p className="mt-1 text-right text-[10px] text-pw-text-muted">
              {notes.length}/{NOTES_MAX}
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              {t('payout.markTransferred.cancel')}
            </Button>
            <Button
              type="submit"
              data-testid="payout-mark-transferred-submit-btn"
              disabled={submitting}
            >
              {submitting
                ? t('payout.markTransferred.submitting')
                : t('payout.markTransferred.submit')}
            </Button>
          </DialogFooter>
        </form>
      </div>
    </Dialog>
  );
}

export default MarkTransferredModal;
